import { useCallback, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Button, Field, Input } from '@mgdlead/ui';

import { AuthLayout } from '../../components/AuthLayout';
import { FormAlert } from '@mgdlead/ui';
import { resetPassword } from '../../lib/auth';
import { resetPasswordSchema } from '../../lib/schemas';
import type { ResetPasswordValues } from '../../lib/schemas';
import { useDocumentTitle } from '../../lib/useDocumentTitle';
import { useZodForm } from '../../lib/useZodForm';

const INITIAL: ResetPasswordValues = { password: '', passwordConfirmation: '' };

export function ResetPasswordPage() {
  useDocumentTitle('Nova senha');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [done, setDone] = useState(false);

  // o Better Auth manda o token na query; sem ele não há o que redefinir
  const token = params.get('token');
  const linkError = params.get('error');

  const onSubmit = useCallback(
    async (values: { password: string; passwordConfirmation: string }) => {
      if (token === null) return;
      await resetPassword({ token, password: values.password });
      setDone(true);
    },
    [token],
  );

  const form = useZodForm({ schema: resetPasswordSchema, initialValues: INITIAL, onSubmit });

  const backToLogin = (
    <Link
      to="/login"
      className="font-medium text-primary-text underline-offset-4 hover:underline"
    >
      Voltar para entrar
    </Link>
  );

  if (token === null || linkError !== null) {
    return (
      <AuthLayout
        title="Link inválido"
        description="Esse link de redefinição expirou, já foi usado ou veio incompleto."
        footer={backToLogin}
      >
        <FormAlert>
          Peça um link novo na tela de recuperação — o anterior deixa de valer assim que outro é
          gerado.
        </FormAlert>
        <Button variant="dark" size="lg" block onClick={() => void navigate('/esqueci-senha')}>
          Pedir novo link
        </Button>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title="Senha alterada" description="Agora é só entrar com a senha nova.">
        <FormAlert tone="success">
          Sua senha foi alterada. As outras sessões abertas continuam válidas — encerre-as em
          Configurações se desconfiar de acesso indevido.
        </FormAlert>
        <Button variant="dark" size="lg" block onClick={() => void navigate('/login', { replace: true })}>
          Entrar
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Nova senha" description="Escolha uma senha que você não use em outro lugar.">
      <form noValidate onSubmit={form.handleSubmit} className="flex flex-col gap-4">
        {form.formError ? <FormAlert>{form.formError}</FormAlert> : null}

        <Field
          label="Nova senha"
          hint="Mínimo de 8 caracteres."
          error={form.errors.password}
          required
        >
          {(control) => (
            <Input
              {...control}
              type="password"
              name="password"
              autoComplete="new-password"
              value={form.values.password}
              invalid={form.errors.password !== undefined}
              onChange={(event) => form.setField('password', event.target.value)}
            />
          )}
        </Field>

        <Field label="Repita a nova senha" error={form.errors.passwordConfirmation} required>
          {(control) => (
            <Input
              {...control}
              type="password"
              name="passwordConfirmation"
              autoComplete="new-password"
              value={form.values.passwordConfirmation}
              invalid={form.errors.passwordConfirmation !== undefined}
              onChange={(event) => form.setField('passwordConfirmation', event.target.value)}
            />
          )}
        </Field>

        <Button
          type="submit"
          variant="dark" size="lg"
          block
          loading={form.pending}
          loadingLabel="Salvando senha"
        >
          Salvar senha
        </Button>
      </form>
    </AuthLayout>
  );
}
